import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
import jwtDecode from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project import
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { openSnackbar } from 'api/snackbar';
import { decryptLocalStorageObj, encryptLocalStorageObj } from 'utils/insightfunctions';
import { getErrorMessage } from 'utils/getErrorMessage';

const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const verifyToken = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded = jwtDecode(serviceToken);
  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);

    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const decryptedData = decryptLocalStorageObj();
        const { token, ...user } = decryptedData;
        if (token && verifyToken(token)) {
          setSession(token);
          await axios.post(process.env.REACT_APP_RTMP_API_URL + '/verifyToken', { token });
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user
            }
          });
        } else {
          localStorage.clear();
          dispatch({
            type: LOGOUT
          });
          openSnackbar({
            open: true,
            message: 'Session Timeout',
            variant: 'alert',
            alert: {
              color: 'error'
            }
          });
        }
      } catch (err) {
        localStorage.clear();
        console.error(err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (response) => {
    try {
       // ⏳ Wait for 10 seconds before finishing
      // console.log('Hold for 10 seconds...');
      // await new Promise((resolve) => setTimeout(resolve, 10000));
      // console.log('10 seconds completed');
      const { user } = response;
      let loginCredentials = {};
      if (user?.id && user?.ParentName) {
        loginCredentials = {
          id: user.id,
          name: user?.ParentName || '',
          selectedCompany: null,
          adminLogin: false,
          token: user.token,
          email: user.email
        };
      } else {
        loginCredentials = {
          id: null,
          name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
          selectedCompany: null,
          adminLogin: true,
          token: user.token,
          email: user.email
        };
      }
      setSession(response.user.token);
      // user.selectedCompany = user?.associatedCompanies?.[0]
      encryptLocalStorageObj(loginCredentials);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: loginCredentials
        }
      });

    } catch (error) {
      const errorMessage = getErrorMessage(error);
      openSnackbar({
        open: true,
        message: errorMessage,
        variant: 'alert',
        alert: {
          color: 'error'
        }
      });
    }
  };

  const changeCompany = async (payload) => {
    const response = await axios.post(process.env.REACT_APP_RTMP_API_URL + '/switch-company', payload);
    const newToken = response.data.token;
    const user = decryptLocalStorageObj();
    user.selectedCompany = payload.CompanyName;
    setSession(newToken);
    encryptLocalStorageObj({ ...user, token: newToken });
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user
      }
    });
  };

  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
    localStorage.removeItem('AID');
  };

  const resetPassword = async () => {};

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider value={{ ...state, login, changeCompany, logout, register, resetPassword, updateProfile }}>
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default JWTContext;
